import { z } from 'zod';

export type WeaponKind = 'sniper' | 'rifle' | 'pistol' | 'smg' | 'heavy' | 'knife' | 'other';

export type Wear = 'FN' | 'MW' | 'FT' | 'WW' | 'BS';

export type ParsedSkinName = {
  weapon: string;
  weaponKind: WeaponKind;
  skinName: string;
  wear: Wear | null;
  isStatTrak: boolean;
  isSouvenir: boolean;
};

const WEAR_MAP: Readonly<Record<string, Wear>> = {
  'Factory New': 'FN',
  'Minimal Wear': 'MW',
  'Field-Tested': 'FT',
  'Well-Worn': 'WW',
  'Battle-Scarred': 'BS',
};

const WEAPON_KIND: Readonly<Record<string, WeaponKind>> = {
  'AK-47': 'rifle',
  'M4A1-S': 'rifle',
  M4A4: 'rifle',
  AUG: 'rifle',
  FAMAS: 'rifle',
  'Galil AR': 'rifle',
  'SG 553': 'rifle',
  AWP: 'sniper',
  'SSG 08': 'sniper',
  G3SG1: 'sniper',
  'SCAR-20': 'sniper',
  'Glock-18': 'pistol',
  'USP-S': 'pistol',
  'Desert Eagle': 'pistol',
  P250: 'pistol',
  'Five-SeveN': 'pistol',
  P2000: 'pistol',
  'CZ75-Auto': 'pistol',
  'Tec-9': 'pistol',
  'R8 Revolver': 'pistol',
  'Dual Berettas': 'pistol',
  MP9: 'smg',
  'MAC-10': 'smg',
  MP7: 'smg',
  'MP5-SD': 'smg',
  'UMP-45': 'smg',
  P90: 'smg',
  'PP-Bizon': 'smg',
  Nova: 'heavy',
  XM1014: 'heavy',
  'Sawed-Off': 'heavy',
  MAG7: 'heavy',
  M249: 'heavy',
  Negev: 'heavy',
};

const WEAR_PATTERN = /\s+\(([^)]+)\)\s*$/;

export function parseSkinMarketName(raw: string): ParsedSkinName {
  let rest = raw.trim();

  let wear: Wear | null = null;
  const wearMatch = rest.match(WEAR_PATTERN);
  if (wearMatch?.[1] && WEAR_MAP[wearMatch[1]]) {
    wear = WEAR_MAP[wearMatch[1]] ?? null;
    rest = rest.slice(0, wearMatch.index ?? 0).trim();
  }

  let isSouvenir = false;
  if (rest.startsWith('Souvenir ')) {
    isSouvenir = true;
    rest = rest.slice('Souvenir '.length);
  }

  let isKnife = false;
  if (rest.charCodeAt(0) === 0x2605) {
    isKnife = true;
    rest = rest.slice(1).trimStart();
  }

  let isStatTrak = false;
  if (rest.startsWith('StatTrak™ ')) {
    isStatTrak = true;
    rest = rest.slice('StatTrak™ '.length);
  }

  const pipe = rest.indexOf(' | ');
  if (pipe === -1) {
    return {
      weapon: rest,
      weaponKind: isKnife ? 'knife' : 'other',
      skinName: '',
      wear,
      isStatTrak,
      isSouvenir,
    };
  }

  const weapon = rest.slice(0, pipe).trim();
  const skinName = rest.slice(pipe + 3).trim();
  const weaponKind: WeaponKind = isKnife ? 'knife' : (WEAPON_KIND[weapon] ?? 'other');

  return { weapon, weaponKind, skinName, wear, isStatTrak, isSouvenir };
}

const WEAR_BANDS: ReadonlyArray<{ max: number; wear: Wear }> = [
  { max: 0.07, wear: 'FN' },
  { max: 0.15, wear: 'MW' },
  { max: 0.38, wear: 'FT' },
  { max: 0.45, wear: 'WW' },
  { max: 1.0, wear: 'BS' },
];

export function wearFromFloat(floatValue: number): Wear {
  for (const band of WEAR_BANDS) {
    if (floatValue < band.max) return band.wear;
  }
  return 'BS';
}

export function formatFloat(floatValue: number, digits = 4): string {
  if (!Number.isFinite(floatValue)) return '—';
  return floatValue.toFixed(digits);
}

const rarityRefSchema = z
  .object({
    id: z.string().nullable().optional(),
    name: z.string().nullable().optional(),
    color: z.string().nullable().optional(),
  })
  .passthrough();

export const bymykelSkinSchema = z
  .object({
    id: z.string().min(1),
    skin_id: z.string().min(1).nullable().optional(),
    name: z.string().min(1),
    description: z.string().nullable().optional(),
    market_hash_name: z.string().nullable().optional(),
    image: z.string().nullable().optional(),
    min_float: z.number().nullable().optional(),
    max_float: z.number().nullable().optional(),
    stattrak: z.boolean().nullable().optional(),
    souvenir: z.boolean().nullable().optional(),
    paint_index: z.string().nullable().optional(),
    rarity: rarityRefSchema.nullable().optional(),
    weapon: z
      .object({
        id: z.string().nullable().optional(),
        weapon_id: z.number().nullable().optional(),
        name: z.string().nullable().optional(),
      })
      .passthrough()
      .nullable()
      .optional(),
    wear: z
      .object({ id: z.string().nullable().optional(), name: z.string().nullable().optional() })
      .passthrough()
      .nullable()
      .optional(),
    legacy_model: z.boolean().nullable().optional(),
  })
  .passthrough();

export type BymykelSkin = z.infer<typeof bymykelSkinSchema>;

export const bymykelStickerSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    description: z.string().nullable().optional(),
    market_hash_name: z.string().nullable().optional(),
    image: z.string().nullable().optional(),
    type: z.string().nullable().optional(),
    effect: z.string().nullable().optional(),
    rarity: rarityRefSchema.nullable().optional(),
    tournament: z
      .object({ id: z.number().nullable().optional(), name: z.string().nullable().optional() })
      .passthrough()
      .nullable()
      .optional(),
  })
  .passthrough();

export type BymykelSticker = z.infer<typeof bymykelStickerSchema>;

export const bymykelCrateSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    description: z.string().nullable().optional(),
    market_hash_name: z.string().nullable().optional(),
    image: z.string().nullable().optional(),
    type: z.string().nullable().optional(),
    first_sale_date: z.string().nullable().optional(),
    rental: z.boolean().nullable().optional(),
    rarity: rarityRefSchema.nullable().optional(),
  })
  .passthrough();

export type BymykelCrate = z.infer<typeof bymykelCrateSchema>;
